// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import { DateValue } from "@/components/shared/datetime-value";
import { LinearIcon } from "@/components/shared/linear-icon";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { OpeningHoursEntry } from "@/features/stores/schedule/opening-hours";
import { formatMonthDay } from "@/lib/format";
import { linearIssueUrl, useLinearLinkMode } from "@/lib/linear";
import { useDateFormat } from "@/lib/preferences";
import { cn } from "@/lib/utils";

/** Weekday abbreviations exactly as `store_opening_hours.day_of_week` stores them, in calendar order. */
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

/**
 * The store's own `open_time` / `close_time` columns, shown as the first row so the default and its
 * per-day overrides read as one list. It is not a `store_opening_hours` row: it is the fallback the
 * resolver uses when none of them match, so *any* `'*'` row displaces it entirely.
 */
type ListEntry = OpeningHoursEntry & { isStoreDefault?: boolean };

/**
 * Which tier an entry sits in, matching the resolution ladder: a more specific tier wins, and
 * within a tier a store-level row beats a chain-level one.
 */
function tierOf(entry: ListEntry): number {
  if (entry.specificDate) return 3;
  if (entry.dayOfYear) return 2;
  if ((entry.dayOfWeek ?? "*") !== "*") return 1;
  return 0;
}

/** The key an entry competes on within its tier — two rows sharing one cover the same days. */
function tierKeyOf(entry: ListEntry): string {
  return entry.specificDate ?? entry.dayOfYear ?? entry.dayOfWeek ?? "*";
}

/** General to specific, then weekdays in calendar order, then store rows above the chain ones. */
function compareEntries(a: ListEntry, b: ListEntry): number {
  const byTier = tierOf(a) - tierOf(b);
  if (byTier !== 0) return byTier;
  if (tierOf(a) === 1) {
    const byWeekday =
      WEEKDAYS.indexOf(a.dayOfWeek as (typeof WEEKDAYS)[number]) -
      WEEKDAYS.indexOf(b.dayOfWeek as (typeof WEEKDAYS)[number]);
    if (byWeekday !== 0) return byWeekday;
  }
  const byKey = tierKeyOf(a).localeCompare(tierKeyOf(b));
  if (byKey !== 0) return byKey;
  // The store's own columns head the default tier — they're what the others override.
  if (a.isStoreDefault !== b.isStoreDefault) return a.isStoreDefault ? -1 : 1;
  return a.scope === b.scope ? 0 : a.scope === "store" ? -1 : 1;
}

/** A row that never applies, because a more specific one covers exactly the same days. */
function isOverridden(entry: ListEntry, entries: ListEntry[]): boolean {
  // Any wildcard row matches every day, so it displaces the store's static columns outright.
  if (entry.isStoreDefault) return entries.some((other) => !other.isStoreDefault && tierOf(other) === 0);
  if (entry.scope !== "chain") return false;
  return entries.some(
    (other) =>
      other.scope === "store" &&
      !other.isStoreDefault &&
      tierOf(other) === tierOf(entry) &&
      tierKeyOf(other) === tierKeyOf(entry),
  );
}

function timeRange(entry: ListEntry): string | null {
  if (!entry.openTime || !entry.closeTime) return null;
  return `${entry.openTime.slice(0, 5)}–${entry.closeTime.slice(0, 5)}`;
}

interface OpeningHoursListProps {
  entries: OpeningHoursEntry[];
  /** The store's `open_time` column, listed as the default row above the per-day entries. */
  defaultOpenTime?: string;
  /** The store's `close_time` column. */
  defaultCloseTime?: string;
}

/**
 * The per-day opening hours that override a store's static open/close times, most general first.
 * Times are `HH:MM[:SS]` time-of-day strings, so they're sliced rather than date-formatted.
 */
export function OpeningHoursList({ entries, defaultOpenTime, defaultCloseTime }: OpeningHoursListProps) {
  const { t } = useTranslation("back-office");
  const [dateFormat] = useDateFormat();
  const linkMode = useLinearLinkMode();

  const storeDefault: ListEntry[] =
    defaultOpenTime && defaultCloseTime
      ? [
          {
            scope: "store",
            dayOfWeek: "*",
            openTime: defaultOpenTime,
            closeTime: defaultCloseTime,
            isStoreDefault: true,
          },
        ]
      : [];
  const all: ListEntry[] = [...storeDefault, ...entries];

  if (all.length === 0) return <span className="text-muted-foreground">—</span>;

  const sorted = [...all].sort(compareEntries);

  return (
    <div className="space-y-1.5">
      {sorted.map((entry, i) => {
        const range = timeRange(entry);
        const overridden = isOverridden(entry, all);
        return (
          <div
            // Rows carry no id of their own, and (tier, key, scope) can legitimately repeat.
            key={i}
            className={cn("flex flex-wrap items-center gap-x-2 gap-y-1", overridden && "opacity-50")}
          >
            <DayLabel entry={entry} dateFormat={dateFormat} />
            <span
              data-slot="opening-hours-range"
              className={cn("font-mono text-sm tabular-nums", !range && "font-sans text-muted-foreground")}
            >
              {range ?? t("stores.info.openingHours.closed")}
            </span>
            {entry.description && <span className="text-sm text-muted-foreground">{entry.description}</span>}
            {entry.tickets?.map((ticket) => (
              <a
                key={ticket}
                href={linearIssueUrl(ticket, linkMode)}
                target={linkMode === "web" ? "_blank" : undefined}
                rel={linkMode === "web" ? "noreferrer" : undefined}
                className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                <LinearIcon className="size-3" />
                {ticket}
              </a>
            ))}
            {entry.scope === "chain" && !entry.isStoreDefault && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="font-normal">
                    {t("stores.info.openingHours.chain")}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>{t("stores.info.openingHours.chainHint")}</TooltipContent>
              </Tooltip>
            )}
            {overridden && (
              // Dimming alone doesn't say *why*, and the store-default row carries no other badge.
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-muted-foreground underline decoration-dotted">
                    {t("stores.info.openingHours.overridden")}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{t("stores.info.openingHours.overriddenHint")}</TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DayLabel({ entry, dateFormat }: { entry: ListEntry; dateFormat: Parameters<typeof formatMonthDay>[1] }) {
  const { t } = useTranslation("back-office");
  if (entry.specificDate) {
    return (
      <Badge variant="outline" className="font-normal">
        <DateValue value={entry.specificDate} className="text-xs" />
      </Badge>
    );
  }
  if (entry.dayOfYear) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className="font-normal tabular-nums">
            {formatMonthDay(entry.dayOfYear, dateFormat)}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>{t("stores.info.openingHours.everyYear")}</TooltipContent>
      </Tooltip>
    );
  }
  const day = entry.dayOfWeek ?? "*";
  if (day === "*") {
    return (
      <Badge variant="outline" className="font-normal">
        {t("stores.info.openingHours.default")}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="font-normal">
      {t(`common.weekdays.${day.toLowerCase()}`, { defaultValue: day })}
    </Badge>
  );
}
